// Offers champion options for Recruit + Invade rewards.

import { CHAMPIONS, type ChampionId, type ChampionTier } from "../../data/champions";
import type { Game } from "../../game/Game";
import { RNG, randomSeed } from "../../game/RNG";

export const RECRUIT_POOL = new Set<ChampionId>([
  "Garen", "Jax", "Warwick",
  "Ezreal", "Ahri", "Jinx",
  "Mundo", "Draven", "Neeko",
  "Darius", "Lux", "KaiSa",
  "Soraka", "Lissandra", "Yuumi",
  "Vayne",
]);

// Per-act recruit tier probabilities (weights, not percentages).
// Act 1: 90% common, 10% rare
// Act 2: 69% common, 30% rare, 1% epic
// Act 3: 60% common, 35% rare, 5% epic
// Act 4: 45% common, 35% rare, 20% epic
// Act 5: 30% common, 30% rare, 39% epic, 1% legendary
// Act 6: 22% common, 25% rare, 51% epic, 2% legendary
// Act 7: 15% common, 20% rare, 62% epic, 3% legendary
function tierWeights(act: number): Partial<Record<ChampionTier, number>> {
  const table: Partial<Record<ChampionTier, number>>[] = [
    { common: 90, rare: 10 },                          // act 1
    { common: 69, rare: 30, epic: 1 },                 // act 2
    { common: 60, rare: 35, epic: 5 },                 // act 3
    { common: 45, rare: 35, epic: 20 },                // act 4
    { common: 30, rare: 30, epic: 39, legendary: 1 },  // act 5
    { common: 22, rare: 25, epic: 51, legendary: 2 },  // act 6
    { common: 15, rare: 20, epic: 62, legendary: 3 },  // act 7
  ];
  return table[Math.min(act, table.length) - 1] ?? table[0];
}

function pickTier(weights: Partial<Record<ChampionTier, number>>, rng: RNG): ChampionTier {
  const entries = Object.entries(weights) as [ChampionTier, number][];
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = rng.next() * total;
  for (const [tier, w] of entries) {
    r -= w;
    if (r <= 0) return tier;
  }
  return entries[entries.length - 1][0];
}

export function getEligiblePool(game: Game): ChampionId[] {
  const state = game.runState;
  const current = new Set<ChampionId>(state?.roster.map((r) => r.championId) ?? []);
  const weights = tierWeights(state?.act ?? 1);
  const eligibleTiers = new Set(Object.keys(weights) as ChampionTier[]);
  return (Object.keys(CHAMPIONS) as ChampionId[]).filter(
    (id) => RECRUIT_POOL.has(id) && !current.has(id) && eligibleTiers.has(CHAMPIONS[id].tier),
  );
}

export function randomChampionOffer(game: Game, count = 2): ChampionId[] {
  const rng = new RNG(randomSeed());
  const state = game.runState;
  const n = state?.recruitsCompleted ?? 0;

  const weights = tierWeights(state?.act ?? 1);

  const threeStarred = new Set<ChampionId>(
    (state?.roster ?? []).filter(r => r.stars === 3).map(r => r.championId),
  );

  const byTier: Partial<Record<ChampionTier, ChampionId[]>> = {};
  for (const id of Object.keys(CHAMPIONS) as ChampionId[]) {
    if (!RECRUIT_POOL.has(id)) continue;
    const tier = CHAMPIONS[id].tier;
    if (!weights[tier]) continue;
    if (threeStarred.has(id)) continue;
    (byTier[tier] ??= []).push(id);
  }

  const offered = new Set<ChampionId>();
  const result: ChampionId[] = [];

  // Ensure first recruit always has one melee + one ranged, respecting tier weights
  if (n === 0 && count >= 2) {
    const pickWithRange = (needsRange: boolean): ChampionId | undefined => {
      const avail: Partial<Record<ChampionTier, number>> = {};
      for (const [tier, w] of Object.entries(weights) as [ChampionTier, number][]) {
        const pool = (byTier[tier] ?? []).filter(
          id => !offered.has(id) && (needsRange ? CHAMPIONS[id].range > 1 : CHAMPIONS[id].range <= 1)
        );
        if (pool.length > 0) avail[tier] = w;
      }
      if (!Object.keys(avail).length) return undefined;
      const tier = pickTier(avail, rng);
      const pool = (byTier[tier] ?? []).filter(
        id => !offered.has(id) && (needsRange ? CHAMPIONS[id].range > 1 : CHAMPIONS[id].range <= 1)
      );
      rng.shuffle(pool);
      return pool[0];
    };

    const ranged = pickWithRange(true);
    if (ranged) offered.add(ranged);
    const melee = pickWithRange(false);

    if (ranged && melee) {
      offered.add(melee);
      const pair = rng.next() < 0.5 ? [ranged, melee] : [melee, ranged];
      result.push(...pair);
    }
  }

  for (let i = result.length; i < count; i++) {
    const available: Partial<Record<ChampionTier, number>> = {};
    for (const [tier, w] of Object.entries(weights) as [ChampionTier, number][]) {
      const pool = byTier[tier] ?? [];
      if (pool.some((id) => !offered.has(id))) available[tier] = w;
    }
    if (Object.keys(available).length === 0) break;

    const tier = pickTier(available, rng);
    const pool = (byTier[tier] ?? []).filter((id) => !offered.has(id));
    rng.shuffle(pool);
    if (pool.length > 0) {
      offered.add(pool[0]);
      result.push(pool[0]);
    }
  }

  return result;
}

export function getTierOdds(game: Game): Record<ChampionTier, number> {
  const state = game.runState;
  const weights = tierWeights(state?.act ?? 1);
  const total = Object.values(weights).reduce((s, w) => s + (w ?? 0), 0);

  const allTiers: ChampionTier[] = ["common", "rare", "epic", "legendary"];
  const odds = {} as Record<ChampionTier, number>;
  for (const tier of allTiers) {
    const w = weights[tier] ?? 0;
    odds[tier] = total > 0 ? Math.round((w / total) * 100) : 0;
  }
  return odds;
}
